import React,
{
  useState,
  useEffect,
  useCallback,
} from "react";

import {
  CalendarRange,
  Filter,
  RefreshCw,
  TrendingUp,
  Activity,
  BarChart3,
  AlertTriangle,
} from "lucide-react";

import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";

import { motion } from "framer-motion";

import api from "../../common/apiClient";
import SummaryApi from "../../common/SummaryApi";
import MISFilterDrawer from "../MISFilterDrawer";


const MonthlyLeadTrendSkeleton = () => {
  return (
    <div className="bg-white rounded-2xl border p-5 animate-pulse">

      <div className="h-6 w-56 bg-gray-200 rounded mb-6" />

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-6">

        {Array.from({ length: 6 }).map((_, index) => (
          <div
            key={index}
            className="h-24 bg-gray-100 rounded-xl"
          />
        ))}

      </div>

      <div className="h-[450px] bg-gray-100 rounded-xl" />

    </div>
  );
};

const MonthlyLeadTrendError = ({
  onRetry,
}) => {
  return (
    <div className="bg-white rounded-2xl border border-red-200 p-6 text-center">

      <h3 className="text-red-600 font-semibold">
        Failed to load Monthly Lead Trend
      </h3>

      <button
        onClick={onRetry}
        className="mt-4 px-4 py-2 rounded-lg bg-red-600 text-white"
      >
        Retry
      </button>

    </div>
  );
};

const MonthlyLeadTrendCard = () => {

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState(false);

  const [data, setData] =
    useState(null);

  const [showFilters, setShowFilters] =
    useState(false);

  const [filters, setFilters] =
    useState({
      filterType: "thisMonth",
      divisionId: "",
      userId: "",
      stageId: "",
      industryType: "",
      fromDate: "",
      toDate: "",
    });

  const [appliedFilters, setAppliedFilters] =
    useState(filters);

  const fetchReport =
    useCallback(async () => {

      try {

        setLoading(true);
        setError(false);

        const params =
          new URLSearchParams();

        Object.entries(
          appliedFilters
        ).forEach(
          ([key, value]) => {

            if (value) {
              params.append(
                key,
                value
              );
            }

          }
        );

        const response =
          await api.get(
            `${SummaryApi.getMonthlyLeadTrend.url}?${params.toString()}`
          );

        if (
          response?.data?.success
        ) {

          setData(
            response.data.data
          );

        }

      } catch (err) {

        console.error(err);
        setError(true);

      } finally {

        setLoading(false);

      }

    }, [appliedFilters]);

  useEffect(() => {

    fetchReport();

  }, [fetchReport]);

  if (loading) {
    return (
      <MonthlyLeadTrendSkeleton />
    );
  }

  if (error) {
    return (
      <MonthlyLeadTrendError
        onRetry={fetchReport}
      />
    );
  }

  const summary =
    data.summary;

  const cards = [
    {
      title:
        "Total Leads",
      value:
        summary.totalLeads,
      icon: Activity,
      color:
        "text-blue-600",
      bg: "bg-blue-50",
    },
    {
      title:
        "Pipeline",
      value: `₹${summary.totalPipeline.toLocaleString("en-IN")}`,
      icon: TrendingUp,
      color:
        "text-green-600",
      bg: "bg-green-50",
    },
    {
      title:
        "Active Months",
      value:
        summary.activeMonths,
      icon: CalendarRange,
      color:
        "text-purple-600",
      bg: "bg-purple-50",
    },
    {
      title:
        "Zero Lead Months",
      value:
        summary.zeroLeadMonths,
      icon: AlertTriangle,
      color:
        "text-red-600",
      bg: "bg-red-50",
    },
    {
      title:
        "Avg / Month",
      value:
        summary.averageLeadsPerMonth,
      icon: BarChart3,
      color:
        "text-orange-600",
      bg: "bg-orange-50",
    },
    {
      title:
        "Best Month",
      value:
        summary.bestMonth,
      icon: TrendingUp,
      color:
        "text-emerald-600",
      bg: "bg-emerald-50",
    },
  ];

  return (
    <>
      <motion.div
        initial={{
          opacity: 0,
          y: 20,
        }}
        whileInView={{
          opacity: 1,
          y: 0,
        }}
        viewport={{
          once: true,
        }}
        transition={{
          duration: 0.4,
        }}
        className="bg-white rounded-2xl border shadow-sm"
      >

        <div className="flex items-center justify-between p-5 border-b">

          <div className="flex items-center gap-2">

            <CalendarRange
              size={18}
              className="text-red-600"
            />

            <h2 className="font-semibold text-lg">
              Monthly Lead Trend
            </h2>

          </div>

          <div className="flex gap-2">

            <button
              onClick={fetchReport}
              className="h-9 w-9 rounded-lg border flex items-center justify-center"
            >
              <RefreshCw size={15} />
            </button>

            <button
              onClick={() =>
                setShowFilters(true)
              }
              className="h-9 w-9 rounded-lg bg-red-600 text-white flex items-center justify-center"
            >
              <Filter size={15} />
            </button>

          </div>

        </div>

        <div className="p-5">

          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">

            {cards.map((item) => {

              const Icon =
                item.icon;

              return (
                <div
                  key={item.title}
                  className={`${item.bg} rounded-xl p-4`}
                >

                  <div className="flex justify-between">

                    <div>

                      <p className="text-xs text-gray-500">
                        {item.title}
                      </p>

                      <h3 className={`mt-2 text-lg font-bold ${item.color}`}>
                        {item.value}
                      </h3>

                    </div>

                    <Icon
                      size={20}
                      className={item.color}
                    />

                  </div>

                </div>
              );

            })}

          </div>

          <div className="border rounded-xl p-4 h-[500px]">

            <h3 className="font-semibold mb-4">
              Lead Generation Trend
            </h3>

            <ResponsiveContainer
              width="100%"
              height="100%"
            >

              <ComposedChart
                data={data.chart}
              >

                <CartesianGrid strokeDasharray="3 3" />

                <XAxis
                  dataKey="month"
                />

                <YAxis
                  yAxisId="left"
                />

                <YAxis
                  yAxisId="right"
                  orientation="right"
                />

                <Tooltip />

                <Legend />

                <Bar
                  yAxisId="left"
                  dataKey="leadCount"
                  fill="#DC2626"
                  radius={[
                    6,
                    6,
                    0,
                    0,
                  ]}
                  name="Leads"
                />

                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="pipelineValue"
                  stroke="#2563EB"
                  strokeWidth={3}
                  name="Pipeline Value"
                />

              </ComposedChart>

            </ResponsiveContainer>

          </div>

          <div className="mt-6 border rounded-xl overflow-hidden">

            <table className="w-full">

              <thead className="bg-gray-50">

                <tr>

                  <th className="px-4 py-3 text-left">
                    Month
                  </th>

                  <th className="px-4 py-3 text-left">
                    Year
                  </th>

                  <th className="px-4 py-3 text-left">
                    Leads
                  </th>

                  <th className="px-4 py-3 text-left">
                    Pipeline
                  </th>

                </tr>

              </thead>

              <tbody>

                {data.chart.map((row, index) => (

                  <tr
                    key={index}
                    className="border-t"
                  >

                    <td className="px-4 py-3">
                      {row.month}
                    </td>

                    <td className="px-4 py-3">
                      {row.year}
                    </td>

                    <td className="px-4 py-3 font-medium">
                      {row.leadCount}
                    </td>

                    <td className="px-4 py-3">
                      ₹{row.pipelineValue.toLocaleString("en-IN")}
                    </td>

                  </tr>

                ))}

              </tbody>

            </table>

          </div>

          <div className="mt-6 bg-red-50 border border-red-200 rounded-xl p-5">

            <h3 className="font-semibold text-red-700 mb-3">
              Management Insights
            </h3>

            <ul className="space-y-2">

              {data.insights.map((item, index) => (

                <li
                  key={index}
                  className="text-sm text-gray-700"
                >
                  • {item}
                </li>

              ))}

            </ul>

          </div>

        </div>

      </motion.div>

      <MISFilterDrawer
        open={showFilters}
        onClose={() =>
          setShowFilters(false)
        }
        filters={filters}
        setFilters={setFilters}
        onApply={() => {

          setAppliedFilters(
            filters
          );

          setShowFilters(false);

        }}
      />
    </>
  );
};

export default MonthlyLeadTrendCard;